// Session-level actions: star (shared, per-conversation) and soft-delete.
// Thin wrappers around /api/conversations/:sessionId{,/star}.

import type { AgentId, SessionId } from '@prakash-software/shared';
import type { InboxPriority, InboxStatus } from '../stores/conversations';
import { apiFetch } from './api';

export interface ConversationAssignee {
  id: AgentId;
  name: string;
  avatarUrl: string | null;
}

export async function listConversationAssignees(
  sessionId: SessionId,
): Promise<ConversationAssignee[]> {
  const result = await apiFetch<{ agents: ConversationAssignee[] }>(
    `/api/conversations/${encodeURIComponent(sessionId)}/assignees`,
  );
  return result.agents;
}

export async function updateConversationInbox(
  sessionId: SessionId,
  patch: {
    status?: InboxStatus;
    priority?: InboxPriority;
    tags?: string[];
    assigneeId?: AgentId | null;
    departmentId?: string | null;
  },
): Promise<{
  status: InboxStatus;
  priority: InboxPriority;
  tags: string[];
  assigneeId: AgentId | null;
  departmentId: string | null;
  department?: { id: string; name: string } | null;
  assignee?: ConversationAssignee | null;
}> {
  return apiFetch(`/api/conversations/${encodeURIComponent(sessionId)}/inbox`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/** Toggle the shared star on a conversation. Any agent on the site may call. */
export async function setSessionStarred(
  sessionId: SessionId,
  starred: boolean,
): Promise<{ starred: boolean }> {
  return apiFetch<{ starred: boolean }>(
    `/api/conversations/${encodeURIComponent(sessionId)}/star`,
    { method: 'PUT', body: JSON.stringify({ starred }) },
  );
}

/** Soft-delete a conversation. Server enforces role (super-admin / site-admin). */
export async function deleteSession(sessionId: SessionId): Promise<{ deleted: boolean }> {
  return apiFetch<{ deleted: boolean }>(
    `/api/conversations/${encodeURIComponent(sessionId)}`,
    { method: 'DELETE' },
  );
}

/**
 * Force-end a session server-side and fire the full session.ended pipeline
 * (webhooks + chat-end email). Called when an agent archives/deletes so the
 * bound follow-up events run reliably. Idempotent server-side.
 */
export async function endConversation(
  sessionId: SessionId,
  reason: 'resolved' | 'transfer' | 'idle' | 'hangup' | 'page-close' = 'resolved',
): Promise<{ ok: boolean; ended: boolean }> {
  return apiFetch<{ ok: boolean; ended: boolean }>(
    `/api/conversations/${encodeURIComponent(sessionId)}/end`,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );
}

export interface SendEmailResult { ok: boolean; status?: string; reason?: string }

/**
 * Send the chat-end email (transcript + notes + summary, per the System
 * template) for this conversation now. Sends even if the global auto-send
 * toggle is off, but still needs the server's SMTP settings. Resolves on 200; the
 * caller catches an ApiError (e.g. 422 no-recipients) for feedback.
 */
export async function sendConversationEmail(sessionId: SessionId): Promise<SendEmailResult> {
  return apiFetch<SendEmailResult>(
    `/api/conversations/${encodeURIComponent(sessionId)}/send-email`,
    { method: 'POST' },
  );
}

/** Manually fire the HTTP webhooks for an event using this session's data
 *  (testing aid). Webhooks only — not the chat-end email. */
export async function triggerConversationWebhook(
  sessionId: SessionId,
  event: 'session.ended' | 'visitor.identified',
): Promise<{ ok: boolean; event: string; fired: number }> {
  return apiFetch<{ ok: boolean; event: string; fired: number }>(
    `/api/conversations/${encodeURIComponent(sessionId)}/trigger`,
    { method: 'POST', body: JSON.stringify({ event }) },
  );
}
